import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CATEGORIES, categoryMeta } from '@/lib/categories';
import { api } from '@/lib/api';
import { IDEA } from '@/constants/testIds';
import { Link2, Type, Sparkles, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

function ModeTab({ value, label, icon: I, testid, currentMode, setMode }) {
    const active = currentMode === value;
    return (
        <button
            type="button"
            onClick={() => setMode(value)}
            data-testid={testid}
            className={`flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-full text-sm font-medium transition ${active ? 'text-white shadow-sm' : 'text-[var(--ns-text2)]'}`}
            style={active ? { background: 'var(--ns-navy)' } : { background: 'transparent' }}
        >
            <I size={14} /> {label}
        </button>
    );
}

export default function SaveIdeaDialog({ open, onOpenChange, tripId = null, onSaved }) {
    const [mode, setMode] = useState('link');
    const [url, setUrl] = useState('');
    const [extracting, setExtracting] = useState(false);
    const [preview, setPreview] = useState(null); // { url, domain, og, ai }
    const [location, setLocation] = useState('');
    const [neighborhood, setNeighborhood] = useState('');
    const [category, setCategory] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setMode('link');
        setUrl('');
        setExtracting(false);
        setPreview(null);
        setLocation('');
        setNeighborhood('');
        setCategory('');
        setNote('');
        setSaving(false);
    };

    useEffect(() => { if (!open) reset(); }, [open]);

    const extract = async () => {
        const trimmed = url.trim();
        if (!trimmed) { toast.error('Paste a link first'); return; }
        setExtracting(true);
        try {
            const { data } = await api.post('/ideas/extract', { url: trimmed });
            setPreview(data);
            const ai = data.ai || {};
            const og = data.og || {};
            setLocation(ai.location || og.title || '');
            setNeighborhood(ai.neighborhood || '');
            setCategory(ai.category || '');
            // ai.summary preserved on preview, used at save time
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not read that link');
        } finally {
            setExtracting(false);
        }
    };

    const submit = async () => {
        if (!location.trim()) { toast.error('Add a location name'); return; }
        if (!category) { toast.error('Pick a category'); return; }
        setSaving(true);
        try {
            const payload = {
                mode,
                location_name: location.trim(),
                category,
                note: note.trim(),
                neighborhood: neighborhood.trim() || null,
                trip_id: tripId,
            };
            if (mode === 'link' && preview) {
                payload.source_url = preview.url;
                payload.source_domain = preview.domain;
                payload.thumbnail = preview.og?.image || null;
                payload.ai_summary = preview.ai?.summary || null;
                payload.best_time = preview.ai?.best_time || null;
            }
            const { data } = await api.post('/ideas', payload);
            toast.success(tripId ? 'Saved to trip' : 'Saved to Inspiration Board');
            onSaved?.(data.idea);
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={IDEA.saveDialog}
                className="rounded-3xl max-w-lg w-[calc(100vw-2rem)] p-0 overflow-hidden border-[var(--ns-border)] max-h-[92vh] overflow-y-auto"
            >
                <div className="p-6 sm:p-8 overflow-hidden">
                    <DialogHeader className="text-left">
                        <DialogTitle className="font-display text-2xl font-semibold tracking-tight">Save an idea</DialogTitle>
                        <DialogDescription className="text-[var(--ns-text2)]">
                            {tripId ? "Pin a new idea to this trip." : "Save somewhere worth visiting — anywhere on the internet."}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex items-center gap-1 p-1 rounded-full mt-6" style={{ background: 'var(--ns-soft)' }}>
                        <ModeTab value="link" label="From a link" icon={Link2} testid={IDEA.modeLinkButton} currentMode={mode} setMode={setMode} />
                        <ModeTab value="manual" label="Type it" icon={Type} testid={IDEA.modeManualButton} currentMode={mode} setMode={setMode} />
                    </div>

                    {mode === 'link' && (
                        <div className="mt-6 space-y-4">
                            <div>
                                <Label className="text-sm text-[var(--ns-text2)]">Paste a link</Label>
                                <div className="flex gap-2 mt-1 min-w-0">
                                    <Input
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                        placeholder="https://www.tiktok.com/... or any URL"
                                        data-testid={IDEA.urlInput}
                                        className="h-12 rounded-xl border-[var(--ns-border)] bg-white"
                                        onKeyDown={(e) => { if (e.key === 'Enter') extract(); }}
                                    />
                                    <Button
                                        onClick={extract}
                                        disabled={extracting}
                                        data-testid={IDEA.extractButton}
                                        className="h-12 rounded-xl px-4 text-white"
                                        style={{ background: 'var(--ns-clay)' }}
                                    >
                                        {extracting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                                    </Button>
                                </div>
                                <p className="text-xs text-[var(--ns-text3)] mt-1.5">We&apos;ll read the page and suggest the location and category.</p>
                            </div>

                            {preview && (
                                <div
                                    data-testid={IDEA.previewCard}
                                    className="rounded-2xl border border-[var(--ns-border)] overflow-hidden bg-white"
                                >
                                    <div className="flex gap-4">
                                        {preview.og?.image ? (
                                            <img
                                                src={preview.og.image}
                                                alt=""
                                                className="w-28 h-28 object-cover flex-shrink-0"
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        ) : (
                                            <div className="w-28 h-28 flex-shrink-0 flex items-center justify-center" style={{ background: 'var(--ns-soft)' }}>
                                                {preview.og?.favicon ? <img src={preview.og.favicon} alt="" className="w-10 h-10" /> : <Link2 className="text-[var(--ns-text3)]" />}
                                            </div>
                                        )}
                                        <div className="py-3 pr-4 min-w-0 flex-1">
                                            <div className="text-[10px] uppercase tracking-widest text-[var(--ns-text3)] flex items-center gap-1 truncate">
                                                <ExternalLink size={10} /> {preview.domain}
                                            </div>
                                            <div className="font-display text-base font-semibold mt-1 truncate">{preview.ai?.location || preview.og?.title}</div>
                                            {preview.ai?.summary && <p className="text-xs text-[var(--ns-text2)] mt-1 line-clamp-2 leading-relaxed">{preview.ai.summary}</p>}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Shared fields */}
                    {(mode === 'manual' || preview) && (
                        <div className="mt-5 space-y-4">
                            <div>
                                <Label className="text-sm text-[var(--ns-text2)]">Location name</Label>
                                <Input
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    placeholder="e.g. Bar Bukowski"
                                    data-testid={IDEA.locationInput}
                                    className="h-12 rounded-xl mt-1 border-[var(--ns-border)] bg-white"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-sm text-[var(--ns-text2)]">Category</Label>
                                    <Select value={category} onValueChange={setCategory}>
                                        <SelectTrigger data-testid={IDEA.categorySelect} className="h-12 rounded-xl mt-1 border-[var(--ns-border)] bg-white">
                                            <SelectValue placeholder="Pick one" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl">
                                            {CATEGORIES.map((c) => {
                                                const Icon = c.icon;
                                                return (
                                                    <SelectItem key={c.value} value={c.value}>
                                                        <span className="inline-flex items-center gap-2"><Icon size={14} style={{ color: c.color }} /> {c.label}</span>
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-sm text-[var(--ns-text2)]">Neighbourhood</Label>
                                    <Input
                                        value={neighborhood}
                                        onChange={(e) => setNeighborhood(e.target.value)}
                                        placeholder="Optional"
                                        data-testid={IDEA.neighborhoodInput}
                                        className="h-12 rounded-xl mt-1 border-[var(--ns-border)] bg-white"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-sm text-[var(--ns-text2)]">Why did you save this?</Label>
                                <Textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="What stood out? Why do you want to go here?"
                                    data-testid={IDEA.noteTextarea}
                                    className="rounded-xl mt-1 border-[var(--ns-border)] bg-white min-h-[96px]"
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter className="mt-7">
                        <Button
                            onClick={submit}
                            disabled={saving || (mode === 'link' && !preview && !location)}
                            data-testid={IDEA.submitButton}
                            className="w-full h-12 rounded-full text-white font-medium"
                            style={{ background: 'var(--ns-navy)' }}
                        >
                            {saving ? 'Saving…' : tripId ? 'Save to trip' : 'Save to Inspiration Board'}
                        </Button>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
}
